import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';

interface MeditationSession {
  id: string;
  date: string;
  duration: number;
  quality: number;
  coherence: number;
  type: string;
}

interface MeditationProgressTrackerProps {
  sessions: MeditationSession[];
  totalHours: number;
  avgQuality: number;
  consciousnessLevel: number;
}

export default function MeditationProgressTracker({ 
  sessions, 
  totalHours, 
  avgQuality, 
  consciousnessLevel 
}: MeditationProgressTrackerProps) {
  const chartData = sessions.slice(-14).map(session => ({
    date: new Date(session.date).toLocaleDateString(),
    quality: session.quality,
    coherence: session.coherence * 10,
    duration: session.duration
  }));

  const getConsciousnessColor = (level: number) => {
    if (level >= 8) return 'bg-purple-500';
    if (level >= 6) return 'bg-blue-500';
    if (level >= 4) return 'bg-green-500';
    return 'bg-yellow-500';
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{totalHours.toFixed(1)}h</div>
            <div className="text-sm text-muted-foreground">Total Practice</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{avgQuality.toFixed(1)}%</div>
            <div className="text-sm text-muted-foreground">Avg Quality</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${getConsciousnessColor(consciousnessLevel)}`} />
              <div className="text-2xl font-bold">Level {consciousnessLevel}</div>
            </div>
            <div className="text-sm text-muted-foreground">Consciousness</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">14-Day Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="date" fontSize={10} />
              <YAxis fontSize={10} />
              <Tooltip />
              <Area 
                type="monotone" 
                dataKey="quality" 
                stackId="1" 
                stroke="#8884d8" 
                fill="#8884d8" 
                fillOpacity={0.6} 
              />
              <Area 
                type="monotone" 
                dataKey="coherence" 
                stackId="2" 
                stroke="#82ca9d" 
                fill="#82ca9d" 
                fillOpacity={0.6} 
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Recent Sessions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {sessions.slice(0, 5).map(session => (
              <div key={session.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{session.type}</Badge>
                  <span className="text-sm">{session.duration}min</span>
                </div>
                <div className="flex items-center gap-2">
                  <Progress value={session.quality} className="w-16 h-2" />
                  <span className="text-xs text-muted-foreground">{session.quality.toFixed(0)}%</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}